import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Badge, Spinner, Nav, NavItem, NavLink, TabContent, TabPane, Card, CardBody, Alert } from 'reactstrap';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import { toast } from 'react-toastify';

const ScoreDetailsModal = ({ isOpen, toggle, applicationId, round, reviewerId }) => {
  const [loading, setLoading] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [activeTab, setActiveTab] = useState('scores');

  useEffect(() => {
    if (isOpen && applicationId && round) {
      fetchScoreDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, applicationId, round, reviewerId]);

  const fetchScoreDetails = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      
      if (round === 'Round 2') {
        if (reviewerId) {
          // Fetch specific reviewer's scores for Round 2
          const [scoresRes, remarksRes] = await Promise.all([
            axios.get(`${BASE_URL}/api/admin/applications/${applicationId}/scores?round=${round}&reviewerId=${reviewerId}`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${BASE_URL}/api/admin/applications/${applicationId}/remarks?round=${round}&reviewerId=${reviewerId}`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          ]);
          
          // Extract final remarks from the remarks response
          const finalRemarksItem = remarksRes.data.data.find(r => r.remarkType === 'final');
          
          setScoreData({
            scores: scoresRes.data.data,
            remarks: remarksRes.data.data,
            finalRemarks: finalRemarksItem ? finalRemarksItem.remarks : null,
            round: round,
            isIndividual: true
          });
        } else {
          // Fetch Round 2 averages (all reviewers)
          const response = await axios.get(
            `${BASE_URL}/api/reviewer/applications/${applicationId}/round2-averages`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setScoreData(response.data.data);
        }
      } else {
        // Fetch Round 1 scores and remarks
        const [scoresRes, remarksRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/admin/applications/${applicationId}/scores?round=${round}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${BASE_URL}/api/admin/applications/${applicationId}/remarks?round=${round}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setScoreData({
          scores: scoresRes.data.data,
          remarks: remarksRes.data.data,
          round: round
        });
      }
    } catch (error) {
      console.error('Error fetching score details:', error);
      toast.error('Failed to load score details');
    } finally {
      setLoading(false);
    }
  };

  const renderRound1Content = () => {
    if (!scoreData) return null;

    const { scores, remarks } = scoreData;
    
    return (
      <>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === 'scores' ? 'active' : ''}
              onClick={() => setActiveTab('scores')}
              style={{ cursor: 'pointer' }}
            >
              Scores
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'remarks' ? 'active' : ''}
              onClick={() => setActiveTab('remarks')}
              style={{ cursor: 'pointer' }}
            >
              Remarks
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="scores">
            {scores && scores.length > 0 ? (
              <>
                <Alert color="info" className="mb-3">
                  <strong>Reviewer:</strong> {scores[0]?.reviewerName} ({scores[0]?.email})
                  {scores[0]?.completed_at && (
                    <span className="ms-3">
                      <strong>Completed:</strong> {new Date(scores[0].completed_at).toLocaleString()}
                    </span>
                  )}
                </Alert>
                <Table bordered responsive>
                <thead className="table-light">
                  <tr>
                    <th>Criterion</th>
                    <th width="12%">Weight</th>
                    <th width="12%">Max Score</th>
                    <th width="12%">Score</th>
                    <th width="12%">Normalized</th>
                    <th width="40%">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {scores.map((score, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold">{score.criterionName}</td>
                      <td>{score.weight}%</td>
                      <td>{score.maxScore}</td>
                      <td>{score.score}</td>
                      <td>
                        <Badge color={Number(score.normalizedScore) >= 70 ? 'success' : Number(score.normalizedScore) >= 50 ? 'warning' : 'danger'}>
                          {Number(score.normalizedScore)?.toFixed(1)}%
                        </Badge>
                      </td>
                      <td>
                        <small style={{ whiteSpace: 'pre-wrap' }}>
                          {score.remarks || <span className="text-muted">No remarks provided</span>}
                        </small>
                      </td>
                    </tr>
                  ))}
                  <tr className="table-success fw-bold">
                    <td colSpan="5" className="text-end">Total Weighted Score:</td>
                    <td>
                      <Badge color="success" className="px-3">
                        {Number(scores[0]?.weightedScore)?.toFixed(2) || 0}%
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </Table>
              
              {/* Final Remarks Section - Show below scores table */}
              {remarks && remarks.filter(r => r.remarkType === 'final').length > 0 && (
                <Card className="mt-3 border-primary">
                  <CardBody>
                    <h6 className="text-primary mb-2">
                      <i className="bi bi-chat-text-fill me-2"></i>
                      Final Overall Remarks
                    </h6>
                    {remarks.filter(r => r.remarkType === 'final').map((remark, idx) => (
                      <div key={idx}>
                        <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {remark.remarks}
                        </p>
                        <small className="text-muted">
                          By: {remark.reviewerName} | {new Date(remark.createdAt).toLocaleString()}
                        </small>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              )}
              </>
            ) : (
              <Alert color="info">No scores available for this round yet.</Alert>
            )}
          </TabPane>

          <TabPane tabId="remarks">
            {remarks && remarks.length > 0 ? (
              <div>
                {/* Final Remarks - Show prominently at the top */}
                {remarks.filter(r => r.remarkType === 'final').map((remark, idx) => (
                  <Card key={`final-${idx}`} className="mb-4 border-primary">
                    <CardBody>
                      <h5 className="text-primary mb-3">
                        <i className="bi bi-chat-text-fill me-2"></i>
                        Final Overall Remarks
                      </h5>
                      <p className="mb-2" style={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
                        {remark.remarks}
                      </p>
                      <small className="text-muted">
                        By: {remark.reviewerName} | {new Date(remark.createdAt).toLocaleString()}
                      </small>
                    </CardBody>
                  </Card>
                ))}
                
                {/* Other Section Remarks */}
                {remarks.filter(r => r.remarkType !== 'final').length > 0 && (
                  <>
                    <h6 className="mb-3">Section-Specific Remarks</h6>
                    {remarks.filter(r => r.remarkType !== 'final').map((remark, idx) => (
                      <Card key={idx} className="mb-3">
                        <CardBody>
                          <h6 className="text-primary mb-2">
                            {remark.remarkType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </h6>
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            {remark.remarks || <span className="text-muted">No remarks provided</span>}
                          </p>
                          <small className="text-muted">
                            By: {remark.reviewerName} | {new Date(remark.createdAt).toLocaleString()}
                          </small>
                        </CardBody>
                      </Card>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <Alert color="info">No remarks available for this round yet.</Alert>
            )}
          </TabPane>
        </TabContent>
      </>
    );
  };

  const renderRound2Content = () => {
    if (!scoreData) return null;

    const { averageScores, individualReviewers, finalWeightedScore, remarks } = scoreData;

    return (
      <>
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === 'scores' ? 'active' : ''}
              onClick={() => setActiveTab('scores')}
              style={{ cursor: 'pointer' }}
            >
              Average Scores
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'individual' ? 'active' : ''}
              onClick={() => setActiveTab('individual')}
              style={{ cursor: 'pointer' }}
            >
              Individual Reviewers
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 'remarks' ? 'active' : ''}
              onClick={() => setActiveTab('remarks')}
              style={{ cursor: 'pointer' }}
            >
              Remarks
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="scores">
            <Alert color="info" className="mb-3">
              <strong>Final Weighted Average:</strong> {' '}
              <Badge color={Number(finalWeightedScore) >= 70 ? 'success' : Number(finalWeightedScore) >= 50 ? 'warning' : 'danger'} className="px-3 py-2">
                {Number(finalWeightedScore)?.toFixed(2)}%
              </Badge>
            </Alert>
            
            {averageScores && averageScores.length > 0 ? (
              <Table bordered responsive>
                <thead className="table-light">
                  <tr>
                    <th>Criterion</th>
                    <th width="12%">Weight</th>
                    <th width="12%">Max Score</th>
                    <th width="15%">Avg Score</th>
                    <th width="15%">Min Score</th>
                    <th width="15%">Max Score</th>
                    <th width="15%">Normalized</th>
                  </tr>
                </thead>
                <tbody>
                  {averageScores.map((score, idx) => (
                    <tr key={idx}>
                      <td className="fw-bold">{score.criterion_name}</td>
                      <td>{score.weight}%</td>
                      <td>{score.max_score}</td>
                      <td>{score.avg_score?.toFixed(2)}</td>
                      <td>{score.min_score}</td>
                      <td>{score.max_score}</td>
                      <td>
                        <Badge color={score.avg_normalized_score >= 70 ? 'success' : score.avg_normalized_score >= 50 ? 'warning' : 'danger'}>
                          {score.avg_normalized_score?.toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert color="info">Round 2 reviews not completed yet.</Alert>
            )}
          </TabPane>

          <TabPane tabId="individual">
            {individualReviewers && individualReviewers.length > 0 ? (
              individualReviewers.map((reviewer, idx) => (
                <Card key={idx} className="mb-3">
                  <CardBody>
                    <h6 className="text-primary mb-3">
                      {reviewer.reviewerName} ({reviewer.reviewerEmail})
                    </h6>
                    <small className="text-muted d-block mb-3">
                      Completed: {new Date(reviewer.completedAt).toLocaleString()}
                    </small>
                    
                    <Table bordered size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Criterion</th>
                          <th width="15%">Score</th>
                          <th width="15%">Normalized</th>
                          <th width="40%">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviewer.scores.map((score, sIdx) => (
                          <tr key={sIdx}>
                            <td>{score.criterionName}</td>
                            <td>{score.score}</td>
                            <td>
                              <Badge color={Number(score.normalizedScore) >= 70 ? 'success' : Number(score.normalizedScore) >= 50 ? 'warning' : 'danger'}>
                                {Number(score.normalizedScore)?.toFixed(1)}%
                              </Badge>
                            </td>
                            <td>
                              <small style={{ whiteSpace: 'pre-wrap' }}>
                                {score.remarks || <span className="text-muted">No remarks</span>}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    
                    {/* Show final remarks if available */}
                    {reviewer.finalRemarks && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <strong className="text-primary d-block mb-2">
                          <i className="bi bi-chat-text-fill me-2"></i>
                          Final Overall Remarks:
                        </strong>
                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                          {reviewer.finalRemarks}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              ))
            ) : (
              <Alert color="info">Round 2 reviews not completed yet.</Alert>
            )}
          </TabPane>

          <TabPane tabId="remarks">
            {remarks && remarks.length > 0 ? (
              remarks.map((reviewer, idx) => (
                <Card key={idx} className="mb-3">
                  <CardBody>
                    <h6 className="text-primary mb-3">
                      Reviewer: {reviewer.reviewerName}
                    </h6>
                    {Object.entries(reviewer.remarks).map(([type, text], rIdx) => {
                      const isFinalRemark = type === 'final';
                      return (
                        <div key={rIdx} className={`mb-3 ${isFinalRemark ? 'p-3 bg-light rounded border-start border-primary border-4' : ''}`}>
                          <strong className={`d-block mb-1 ${isFinalRemark ? 'text-primary' : ''}`}>
                            {isFinalRemark && <i className="bi bi-chat-text-fill me-2"></i>}
                            {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                          </strong>
                          <p className={`mb-0 ${isFinalRemark ? '' : 'ms-3'}`} style={{ whiteSpace: 'pre-wrap', fontSize: isFinalRemark ? '1rem' : 'inherit' }}>
                            {text || <span className="text-muted">No remarks</span>}
                          </p>
                        </div>
                      );
                    })}
                  </CardBody>
                </Card>
              ))
            ) : (
              <Alert color="info">No remarks available yet.</Alert>
            )}
          </TabPane>
        </TabContent>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {round} Score Details - Application #{applicationId}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Loading score details...</p>
          </div>
        ) : (
          <>
            {round === 'Round 2' && scoreData?.isIndividual ? renderRound1Content() : round === 'Round 2' ? renderRound2Content() : renderRound1Content()}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ScoreDetailsModal;
